/* eslint react/style-prop-object: 0 */
import React from 'react';
import num from 'numeral';
import { Grid, Empty } from 'antd';
import ResponsiveBar from 'Common/Components/LazyResponsiveBar';
import { FormattedMessage, useIntl } from 'react-intl';
import withLoading from 'Common/HOC/loadingOr';

import {
  ReportType,
  RevenueAndProfitChartData,
} from 'utils/apiClient/services/reports/types';

interface Props {
  currency: string;
  data?: RevenueAndProfitChartData[];
}

const NoData = () => (
  <Empty
    image={Empty.PRESENTED_IMAGE_SIMPLE}
    description={<FormattedMessage defaultMessage="No data" />}
  />
);

const getHideYAxis = (
  screens: ReturnType<typeof Grid.useBreakpoint>,
  dataLength: number,
  reportType: ReportType
) => {
  if (screens.xl) {
    if (reportType === 'hourly') {
      return dataLength > 24;
    }
    return dataLength > 40;
  }
  return dataLength > 20;
};

const RevenueOverviewRevenueAndProfitChartView: React.FC<Props> = ({
  currency,
  data,
}) => {
  const intl = useIntl();
  const screens = Grid.useBreakpoint();

  const hideYAxis = getHideYAxis(screens, data.length, data[0]?.type);

  if (!data || data.length === 0) {
    return <NoData />;
  }
  return (
    <ResponsiveBar
      data={data.map((d) => ({ ...d, revenue: d.revenue - d.profit }))}
      keys={['profit', 'revenue']}
      indexBy="date"
      margin={{ top: 35, right: 20, bottom: 20, left: 70 }}
      padding={0.6}
      colors={(item) => (item.id === 'revenue' ? '#dac0d2' : '#c094b2')}
      enableLabel={false}
      legends={[
        {
          dataFrom: 'keys',
          anchor: 'top',
          direction: 'row',
          justify: false,
          itemWidth: 70,
          itemHeight: 25,
          translateY: -32,
          itemsSpacing: 10,
          symbolShape: 'circle',
          symbolSize: 20,
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 0.6,
              },
            },
          ],
          data: [
            {
              id: 'profit',
              color: '#c094b2',
              label: intl.formatMessage({
                defaultMessage: 'profit',
              }),
            },
            {
              id: 'revenue',
              color: '#dac0d2',
              label: intl.formatMessage({ defaultMessage: 'revenue' }),
            },
          ],
        },
      ]}
      axisLeft={{
        format: (value) =>
          intl.formatNumber(Number(value), {
            style: 'currency',
            currency,
          }),
      }}
      tooltip={({ data: item }) => (
        <div
          css={`
            background: #333;
            color: #fff;
            padding: 4px 6px;
            border-radius: 2px;
          `}
        >
          <FormattedMessage
            defaultMessage="{label}: {date} { breakline } Revenue: {revenue} {breakline} Margin: {profit}"
            values={{
              breakline: <br />,
              label:
                item.type === 'weekly'
                  ? intl.formatMessage({ defaultMessage: 'Week number' })
                  : intl.formatMessage({ defaultMessage: 'Date' }),
              date: item.originalDate,
              revenue: intl.formatNumber(
                num(item.revenue).add(item.profit).value() as number,
                {
                  style: 'currency',
                  currency,
                }
              ),
              profit: intl.formatNumber(Number(item.profit), {
                style: 'currency',
                currency,
              }),
            }}
          />
        </div>
      )}
      axisBottom={hideYAxis ? null : {}}
      theme={{
        axis: {
          ticks: {
            text: {
              fontSize: 9,
            },
          },
        },
      }}
    />
  );
};

export default withLoading(RevenueOverviewRevenueAndProfitChartView);
